import { takeLatest, put, select, all, call } from 'redux-saga/effects';
import { get } from 'lodash';
import { dashboardsSelector } from '@store/tempDashboard/selectors';
import { applyFilter, updateInterval } from '@store/common/actions';
import { resetDrillDown } from '@store/summaryContainer/drilldown/actions';
import { bleEnabledSelector } from '@store/common/selectors';
import { setMetrics, selectedMetricChart } from '@store/metricsChartContainer/actions';
import { setSlice, setDefaultSlice } from '../../birdChart/actions';
import { setSelectedEvent } from '../../alerts/actions';
import { changeMode } from '../../actions';
import * as types from './types';
import { MODE_ALERT_DIAGNOSTICS } from '../../constants';
import moment from 'moment';
import { setSharedState, setGlobalOrderByMetric } from '@store/trends/base/actions';
import { parseAlert } from '@components/alert-panel/ManualAlert/hooks/utils';
import { globalRouter } from '@src/utils/globalRouter';

const { baseUrl } = window.conviva;

function sleep(time) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), time);
  });
}

function* initTempDashboard({ tempDashboardId }) {
  const dashboards = yield select(dashboardsSelector);
  const tempDashboard = dashboards[tempDashboardId];
  if (!tempDashboard || !tempDashboard.dashboard) {
    globalRouter.navigate(`${baseUrl}/dashboard/trends`, { replace: true });
    return;
  }
  yield put({ type: types.START_FETCHING });
  const { dashboard } = tempDashboard;
  try {
    const {
      interval: { from, to },
      appliedFilters,
    } = get(dashboard, 'common');
    const alerts = get(dashboard, 'module:trends.alerts');
    const { selectedEvent } = alerts;
    const isBusinessMetricsEnabled = yield select(bleEnabledSelector);

    // workaround for old/new drilldown compatiblity
    // TODO: cleanup manual alert page to use new format?
    if (!('_metric' in selectedEvent.alert)) {
      selectedEvent.alert = parseAlert(selectedEvent.alert, isBusinessMetricsEnabled);
    }

    const { alert, time, window } = selectedEvent;
    const { metric, cmMetric } = alert;

    const timeBucket = window * 60000;
    const ts = new Date(time).getTime();

    // CE-7390 : Slice on Manual alert diagnostic should be for the start-end time of the alert
    // Alert duration is calculated as EndTime - window
    const endTime = Math.min(ts, new Date().getTime());
    const startTime = endTime - timeBucket;

    const metricList = [metric];
    if (cmMetric) {
      metricList.push(cmMetric);
    }

    const tasks = [
      put(changeMode(MODE_ALERT_DIAGNOSTICS)),
      put(updateInterval(from, to, 'custom', 'trends')),
      put(setSharedState({})), // resets base state
      put(setSelectedEvent(selectedEvent)),
      put(selectedMetricChart(metric)),
      put(setMetrics(metricList)),
      put(setDefaultSlice([0, 100], [startTime, endTime])),
      /**
       * https://conviva.atlassian.net/browse/CE-11466
       * moving end time to start of next minute as MGW excludes last minute timestamp
       */
      put(
        setSlice(
          [0, 100],
          [startTime, moment(endTime).startOf('minute').add(1, 'minute').unix() * 1000],
        ),
      ),
      put(resetDrillDown([])),
      put(applyFilter(appliedFilters[0])),
    ];

    if (cmMetric) {
      tasks.push(put(setGlobalOrderByMetric(cmMetric)));
    }
    yield all(tasks);
    yield sleep(300);
    yield put({ type: types.END_FETCHING });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* watchInitTempDashboard() {
  yield takeLatest([types.INIT_TEMP_DASHBOARD], initTempDashboard);
}

export default [watchInitTempDashboard];
